import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { fmtBRL, fmtKg, fmtNum } from "@/components/ui/Primitives";
import { Printer, X, FileText } from "lucide-react";

const CAT_LABELS = [
  { k: "cx1",  label: "CX1",  subtitle: "Refugo" },
  { k: "cx2",  label: "CX2",  subtitle: "Pequena" },
  { k: "cx3",  label: "CX3",  subtitle: "Média" },
  { k: "cx3g", label: "CX3G", subtitle: "Grande" },
  { k: "cx4",  label: "CX4",  subtitle: "Extra" },
];

function fmtDate(iso) {
  if (!iso) return "—";
  const d = String(iso).slice(0, 10);
  const [y, m, dd] = d.split("-");
  return `${dd}/${m}/${y}`;
}

export default function Romaneio() {
  const { id } = useParams();
  const nav = useNavigate();
  const [inv, setInv] = useState(null);
  const [company, setCompany] = useState(null);
  const [pallets, setPallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [{ data: i }, { data: c }, { data: p }] = await Promise.all([
          api.get(`/invoices/${id}`),
          api.get(`/company`),
          api.get(`/invoices/${id}/pallets`),
        ]);
        setInv(i); setCompany(c); setPallets(p || []);
      } catch (e) { setErr(formatErr(e)); }
      finally { setLoading(false); }
    })();
  }, [id]);

  const doPrint = () => window.print();

  if (loading) return <div className="p-8 text-center text-ink-500">Carregando romaneio...</div>;
  if (err) return <div className="p-8"><div className="text-danger">{err}</div></div>;
  if (!inv) return <div className="p-8">NF não encontrada.</div>;

  const benef = inv.beneficiamento || {};
  const custos = benef.custos || {};
  const isBalancao = benef.modo_precificacao === "balancao";
  const totalCaixas = Number(inv.total_caixas) || 0;
  const pesoLiquido = Number(inv.peso_liquido) || 0;
  const quebraKg = Number(inv.quebra_kg) || 0;
  const quebraPct = pesoLiquido > 0 && totalCaixas > 0 ? (quebraKg / pesoLiquido) * 100 : 0;
  const catsWithData = CAT_LABELS.map((c) => {
    const b = benef[c.k] || {};
    const q = Number(b.quantidade || 0);
    const p = Number(b.preco || 0);
    return { ...c, quantidade: q, preco: p, subtotal: q * p, pct: totalCaixas > 0 ? (q / totalCaixas) * 100 : 0 };
  });
  const pesoPagavel = isBalancao ? Number(inv.peso_pagavel_balancao || pesoLiquido) : pesoLiquido;
  const descPct = Number(benef.desconto_balancao_pct || 0);
  const precoKg = Number(benef.preco_balancao || 0);

  return (
    <div className="min-h-screen bg-stone-100 print:bg-white">
      {/* Toolbar (some na impressão) */}
      <div className="print:hidden bg-white border-b border-ink-200 px-4 py-3 flex items-center justify-between sticky top-0 z-10">
        <button onClick={() => nav(-1)} className="p-2 hover:bg-stone-100 rounded flex items-center gap-2 text-sm" data-testid="close-romaneio">
          <X size={16} /> Fechar
        </button>
        <div className="text-sm text-ink-700 font-semibold flex items-center gap-2">
          <FileText size={16} /> Romaneio de Beneficiamento — NF #{inv.nota_numero}
        </div>
        <button onClick={doPrint} className="px-4 py-2 rounded-md bg-brand-500 text-white text-sm font-semibold hover:opacity-90 flex items-center gap-2" data-testid="print-romaneio-btn">
          <Printer size={16} /> Imprimir
        </button>
      </div>

      {/* Folha A4 */}
      <div className="mx-auto my-6 print:my-0 bg-white print:bg-white shadow-lg print:shadow-none max-w-4xl print:max-w-none w-full" style={{ minHeight: "297mm" }} data-testid="romaneio-sheet">
        <div className="p-8 print:p-6 space-y-4">
          {/* Cabeçalho da empresa */}
          <header className="border-b-2 border-ink-900 pb-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <h1 className="text-2xl font-bold text-ink-900 leading-tight uppercase tracking-tight">
                  {company?.razao_social || "Cebolas Serenata Ltda"}
                </h1>
                {company?.nome_fantasia && (
                  <div className="text-sm text-ink-500">{company.nome_fantasia}</div>
                )}
                <div className="mt-2 text-xs text-ink-700 space-y-0.5">
                  <div><span className="text-ink-500">CNPJ:</span> <span className="font-mono">{company?.cnpj || "—"}</span>
                    {company?.ie && <> · <span className="text-ink-500">IE:</span> <span className="font-mono">{company.ie}</span></>}
                  </div>
                  <div>
                    {company?.endereco_logradouro || "—"}
                    {company?.endereco_bairro && ` · ${company.endereco_bairro}`}
                    {company?.endereco_cidade && ` · ${company.endereco_cidade}/${company.endereco_uf}`}
                    {company?.endereco_cep && ` · CEP ${company.endereco_cep}`}
                  </div>
                  <div className="flex gap-3">
                    {company?.telefone && <span><span className="text-ink-500">Tel:</span> {company.telefone}</span>}
                    {company?.email && <span><span className="text-ink-500">E-mail:</span> {company.email}</span>}
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs uppercase tracking-widest text-ink-500 font-bold">Romaneio nº</div>
                <div className="text-xl font-mono font-bold text-ink-900">RM-{String(inv.romaneio_numero || 0).padStart(4, "0")}</div>
                <div className="text-xs text-ink-500 mt-2">Emitido em</div>
                <div className="text-sm font-mono">{fmtDate(new Date().toISOString())}</div>
              </div>
            </div>
          </header>

          {/* Título */}
          <div className="text-center py-2">
            <h2 className="text-lg font-bold uppercase tracking-widest text-ink-900">Romaneio de Beneficiamento</h2>
          </div>

          {/* Info NF + Fornecedor */}
          <section className="grid grid-cols-2 gap-4 border border-ink-300 rounded p-3">
            <div className="space-y-1 text-sm">
              <div className="text-xs uppercase tracking-wider text-ink-500 font-bold mb-1">Nota Fiscal / Entrada</div>
              <InfoRow label="Nº NF" value={`#${inv.nota_numero}`} mono bold />
              <InfoRow label="Data Entrada" value={fmtDate(inv.data_entrada)} mono />
              <InfoRow label="Tipo" value={<span className="capitalize">{inv.tipo_cebola}</span>} />
              <InfoRow label="Origem" value={inv.origem || "—"} />
              <InfoRow label="Motorista" value={inv.motorista || "—"} />
              <InfoRow label="Placa" value={inv.placa || "—"} mono />
            </div>
            <div className="space-y-1 text-sm">
              <div className="text-xs uppercase tracking-wider text-ink-500 font-bold mb-1">Produtor / Fornecedor</div>
              <InfoRow label="Nome" value={inv.fornecedor || "—"} bold />
              <InfoRow label="Data Beneficiamento" value={fmtDate(inv.data_beneficiamento)} mono />
              <InfoRow label="Vencimento Pgto" value={fmtDate(inv.vencimento_fornecedor)} mono />
              <InfoRow label="Modo Precificação" value={isBalancao ? "Balanção" : "Boca da Máquina"} bold />
              {inv.observacoes && (
                <div className="pt-1">
                  <div className="text-xs text-ink-500">Observações</div>
                  <div className="text-sm text-ink-700 italic">{inv.observacoes}</div>
                </div>
              )}
            </div>
          </section>

          {/* Pesagem */}
          <section className="border border-ink-300 rounded p-3">
            <div className="text-xs uppercase tracking-wider text-ink-500 font-bold mb-2">Pesagem</div>
            <div className={`grid ${isBalancao ? "grid-cols-3" : "grid-cols-5"} gap-3 text-sm`}>
              <PesagemCell label="Peso Bruto" value={fmtKg(inv.peso_bruto)} />
              <PesagemCell label="Tara" value={fmtKg(inv.peso_tara)} />
              <PesagemCell label="Peso Líquido" value={fmtKg(pesoLiquido)} highlight />
              {!isBalancao && (
                <>
                  <PesagemCell label="Peso Embalado" value={fmtKg(totalCaixas * 20)} />
                  <PesagemCell label={`Quebra${quebraPct > 0 ? ` (${quebraPct.toFixed(1)}%)` : ""}`} value={fmtKg(quebraKg)} danger={quebraPct > 8} />
                </>
              )}
            </div>
          </section>

          {/* Beneficiamento — tabela categorias (só Boca da Máquina) */}
          {!isBalancao && (
          <section>
            <div className="text-xs uppercase tracking-wider text-ink-500 font-bold mb-2">Produto Beneficiado</div>
            <table className="w-full border border-ink-300 rounded overflow-hidden text-sm">
              <thead className="bg-stone-100">
                <tr className="text-left border-b border-ink-300">
                  <th className="px-3 py-2 font-bold text-xs uppercase">Categoria</th>
                  <th className="px-3 py-2 font-bold text-xs uppercase">Descrição</th>
                  <th className="px-3 py-2 font-bold text-xs uppercase text-right">Quantidade</th>
                  <th className="px-3 py-2 font-bold text-xs uppercase text-right">%</th>
                  {!isBalancao && (
                    <>
                      <th className="px-3 py-2 font-bold text-xs uppercase text-right">Preço Unit.</th>
                      <th className="px-3 py-2 font-bold text-xs uppercase text-right">Subtotal</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {catsWithData.filter((c) => c.quantidade > 0).map((c) => (
                  <tr key={c.k} className="border-b border-ink-200 last:border-0">
                    <td className="px-3 py-2 font-mono font-semibold">{c.label}</td>
                    <td className="px-3 py-2 text-ink-700 capitalize">Cebola {inv.tipo_cebola}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmtNum(c.quantidade, 0)}</td>
                    <td className="px-3 py-2 text-right font-mono">{c.pct.toFixed(1)}%</td>
                    {!isBalancao && (
                      <>
                        <td className="px-3 py-2 text-right font-mono">{fmtBRL(c.preco)}</td>
                        <td className="px-3 py-2 text-right font-mono">{fmtBRL(c.subtotal)}</td>
                      </>
                    )}
                  </tr>
                ))}
                <tr className="bg-stone-50 font-bold border-t-2 border-ink-900">
                  <td className="px-3 py-2" colSpan={2}>TOTAL</td>
                  <td className="px-3 py-2 text-right font-mono">{fmtNum(totalCaixas, 0)}</td>
                  <td className="px-3 py-2 text-right font-mono">100%</td>
                  {!isBalancao && (
                    <>
                      <td className="px-3 py-2 text-right">—</td>
                      <td className="px-3 py-2 text-right font-mono text-brand-500">{fmtBRL(inv.valor_total_caixas)}</td>
                    </>
                  )}
                </tr>
              </tbody>
            </table>
          </section>
          )}

          {/* Se modo balanção, mostra a base do cálculo */}
          {isBalancao && (
            <section className="border border-sky-300 rounded p-3 bg-sky-50 text-sm">
              <div className="text-xs uppercase tracking-wider text-sky-700 font-bold mb-2">Cálculo — Modo Balanção</div>
              <div className="grid grid-cols-4 gap-3">
                <div>
                  <div className="text-xs text-ink-500">Peso Líquido</div>
                  <div className="font-mono font-semibold">{fmtKg(pesoLiquido)}</div>
                </div>
                <div>
                  <div className="text-xs text-ink-500">Desconto</div>
                  <div className="font-mono">{descPct.toFixed(2)}%</div>
                </div>
                <div>
                  <div className="text-xs text-ink-500">Peso Pagável</div>
                  <div className="font-mono font-semibold">{fmtKg(pesoPagavel)}</div>
                </div>
                <div>
                  <div className="text-xs text-ink-500">R$ por kg</div>
                  <div className="font-mono font-semibold">{fmtBRL(precoKg)}</div>
                </div>
              </div>
            </section>
          )}

          {/* Totalizadores financeiros */}
          <section className="border-2 border-ink-900 rounded p-4">
            <div className="text-xs uppercase tracking-wider text-ink-500 font-bold mb-3">Resumo Financeiro</div>
            <div className="max-w-md">
              <div className="text-xs uppercase tracking-wider text-emerald-700 font-bold mb-2">Pagamento ao Produtor</div>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between"><span>Valor Bruto</span><span className="font-mono">{fmtBRL(inv.valor_total_caixas)}</span></div>
                <div className="flex justify-between text-danger"><span>Funrural ({custos.fun_rural_pct || 0}%)</span><span className="font-mono">-{fmtBRL(inv.funrural)}</span></div>
                <div className="border-t border-ink-300 pt-1 mt-1 flex justify-between font-bold text-base text-emerald-700">
                  <span>LÍQUIDO A PAGAR</span>
                  <span className="font-mono">{fmtBRL(inv.valor_liquido_fornecedor)}</span>
                </div>
              </div>
            </div>
          </section>

          {/* Assinaturas */}
          <section className="pt-8 print:pt-12">
            <div className="grid grid-cols-2 gap-8">
              <div className="text-center">
                <div className="border-t border-ink-900 pt-1 mt-8"></div>
                <div className="text-xs uppercase tracking-widest text-ink-700 font-bold">Produtor / Fornecedor</div>
                <div className="text-sm text-ink-500">{inv.fornecedor}</div>
              </div>
              <div className="text-center">
                <div className="border-t border-ink-900 pt-1 mt-8"></div>
                <div className="text-xs uppercase tracking-widest text-ink-700 font-bold">Responsável — {company?.nome_fantasia || "Cebolas Serenata"}</div>
                <div className="text-sm text-ink-500">Data: ____/____/________</div>
              </div>
            </div>
          </section>

          {/* Footer */}
          <footer className="pt-4 text-center text-[10px] text-ink-500 border-t border-ink-200">
            Documento emitido eletronicamente por {company?.nome_fantasia || "Cebolas Serenata"} · Sistema ERP
          </footer>
        </div>
      </div>

      <style>{`
        @media print {
          @page { size: A4; margin: 10mm; }
          body { margin: 0; }
        }
      `}</style>
    </div>
  );
}

function InfoRow({ label, value, mono, bold }) {
  return (
    <div className="flex text-sm">
      <span className="text-ink-500 w-36 flex-shrink-0">{label}:</span>
      <span className={`${mono ? "font-mono" : ""} ${bold ? "font-bold text-ink-900" : "text-ink-700"} truncate`}>{value}</span>
    </div>
  );
}

function PesagemCell({ label, value, highlight, danger }) {
  return (
    <div className={`p-2 rounded ${highlight ? "bg-emerald-50 border border-emerald-300" : danger ? "bg-red-50 border border-red-300" : "bg-stone-50 border border-ink-200"}`}>
      <div className="text-[10px] uppercase tracking-wider text-ink-500 font-bold">{label}</div>
      <div className={`font-mono font-semibold ${highlight ? "text-emerald-700" : danger ? "text-danger" : "text-ink-900"}`}>{value}</div>
    </div>
  );
}
